import React from 'react';
import { useParams } from 'react-router-dom';
import serviceArticles from '../common/serviceArticle';
import { Helmet } from "react-helmet";
import { FaCarSide, FaMotorcycle, FaTruck, FaWrench, FaRocket, FaMapLocation } from 'react-icons/fa6';

export default function ServiceArticle() {
    const { serviceName } = useParams();
    const service = serviceArticles.find(article => article.service_name === decodeURIComponent(serviceName));
    const icons = {
        "Pengiriman Mobil": <FaCarSide className='w-16 h-16 md:w-24 md:h-24 text-gray-800' />,
        "Pengiriman Motor": <FaMotorcycle className='w-16 h-16 md:w-24 md:h-24 text-gray-800' />,
        "Beban Berat": <FaTruck className='w-16 h-16 md:w-24 md:h-24 text-gray-800' />,
        "Layanan Darurat": <FaWrench className='w-16 h-16 md:w-24 md:h-24 text-gray-800' />,
        "Cepat Sampai Tujuan": <FaRocket className='w-16 h-16 md:w-24 md:h-24 text-gray-800' />,
        "Towing Jarak Jauh": <FaMapLocation className='w-16 h-16 md:w-24 md:h-24 text-gray-800' />
    };

    if (!service) {
        return (
            <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen w-full flex items-center justify-center">
                <div className="text-center p-8 bg-white rounded-xl shadow-lg">
                    <h2 className="text-3xl font-[PoppinsB] text-gray-800 mb-4">Service Not Found</h2>
                    <p className="text-gray-600">The requested service could not be found.</p>
                </div>
            </div>
        );
    }

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": service.title,
        "description": service.meta_description,
        "author": {
            "@type": "Person",
            "name": "BBYSHIN",
        },
        "publisher": {
            "@type": "Organization",
            "name": "BBYSHIN",
            "logo": {
                "@type": "ImageObject",
                "url": "https://bbyshintowing.com/logo.png", // Replace with your actual logo URL
            },
        },
        "datePublished": new Date().toISOString(), // Replace with the actual publication date
        "image": "https://bbyshintowing.com/default-image.jpg", // Replace with your actual image URL
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://bbyshintowing.com/service/${serviceName}`, // Replace with your actual page URL
        },
    };

    return (
        <>
            <Helmet>
                <title>{`${service.title} - Professional Towing Services | BBYSHIN TOWING`}</title>
                <meta name="description" content={service.meta_description} />
                <meta name="keywords" content={`towing service, ${service.service_name.toLowerCase()}, professional towing, 24/7 towing, emergency towing`} />
                <meta property="og:title" content={`${service.title} | BBYSHIN TOWING`} />
                <meta property="og:description" content={service.meta_description} />
                <meta property="og:type" content="article" />
                <link rel="canonical" href={window.location.href} />
                {/* Add structured data */}
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>
            <main className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen w-full">
                <article className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16' itemScope itemType="http://schema.org/Article">
                    <header className='text-center mb-16'>
                        <div className="flex justify-center mb-8 transform hover:scale-105 transition-transform duration-300">
                            {service.title && icons[service.service_name]}
                        </div>
                        <h1 className='text-4xl sm:text-6xl font-[PoppinsB] text-gray-800 tracking-wide mb-6 leading-tight' itemProp="headline">{service.title}</h1>
                        <p className="text-gray-700 text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed" itemProp="description">{service.article_body.intro}</p>
                    </header>

                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-16 hover:shadow-2xl transition-shadow duration-300">
                        <section className='prose prose-lg md:prose-xl max-w-4xl mx-auto'>
                            <h2 className="text-3xl md:text-4xl font-[PoppinsB] text-gray-800 mb-8">{service.article_body.why_choose_us.title}</h2>
                            <ul className="list-none space-y-8">
                                {service.article_body.why_choose_us.points.map((point, index) => (
                                    <li key={index} className="flex items-start gap-6 hover:transform hover:translate-x-2 transition-transform duration-300">
                                        <div className="flex-shrink-0 w-10 h-10 bg-[#fcfc00] rounded-full flex items-center justify-center shadow-md">
                                            <span className="font-bold text-lg">{index + 1}</span>
                                        </div>
                                        <div>
                                            <strong className="block text-2xl mb-2">{point.title}</strong>
                                            <p className="text-gray-600 leading-relaxed">{point.description}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>

                    <section className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-16">
                        <h2 className="text-3xl md:text-4xl font-[PoppinsB] text-gray-800 mb-8 text-center">Layanan Kami</h2>
                        <div className="grid gap-8 md:grid-cols-2">
                            {service.article_body.services.map((service, index) => (
                                <div key={index} className="p-8 border-2 rounded-xl hover:shadow-xl transition-all duration-300 hover:border-[#fcfc00]">
                                    <h3 className="font-[PoppinsB] text-2xl mb-4">{service.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <div className="mt-16 text-center">
                        <a
                            href={service.article_body.cta.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 px-10 py-5 bg-[#fcfc00] hover:bg-[#e6e600] text-gray-800 rounded-full font-medium transition-all duration-300 text-xl shadow-lg hover:shadow-2xl transform hover:scale-105"
                        >
                            {service.article_body.cta.button_text}
                            <span className="ml-2">→</span>
                        </a>
                    </div>
                    <p class="text-xs text-gray-400 mt-8">
                            This article was created using advanced writing tools and reviewed by a content editor for accuracy and relevance.
                        </p>
                </article>
            </main>
        </>
    );
}